import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { m } from 'framer-motion'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import MakeInformedDecisionsImage from './MakeInformedDecisionsImage'

const copy = {
  heading: `Make informed decisions with clean and compliant customer data`,
}

const list = [
  {
    title: `Trust`,
    description: `Power your tools and product cross-sell recommendations with clean, reliable data.`,
  },
  {
    title: `Empower`,
    description: `Align your teams and tools with secure and consistent customer data.`,
  },
  {
    title: `Protect`,
    description: `Simplify compliance with automated data governance and real-time PII screening.`,
  },
]

const link = {
  href: `/product/protocols/`,
  text: `Explore Protocols`,
}

const MakeInformedDecisions: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <SectionBase variant="2Col" reverse={true} copyVariant="narrow" sticky={true}>
        <Section.Copy>
          <CopyTextBase>
            <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
          </CopyTextBase>
          <ColumnTrackWrapper>
            <ColumnTrack gap="medium">
              {list.map(({ title, description }) => (
                <FeatureBoxBase alignment="left" key={title}>
                  <FeatureBox.Body>
                    <FeatureBox.Title tag="h3" variant="h5">
                      {title}
                    </FeatureBox.Title>
                    <FeatureBox.Text>{description}</FeatureBox.Text>
                  </FeatureBox.Body>
                </FeatureBoxBase>
              ))}
              <ButtonWrapper>
                <ButtonText
                  tag="a"
                  href={getAbsoluteUrl(link.href, pageConfig)}
                  target="_blank"
                  iconEnd={ChevronRightIcon}
                >
                  {link.text}
                </ButtonText>
              </ButtonWrapper>
            </ColumnTrack>
          </ColumnTrackWrapper>
        </Section.Copy>
        <Section.Contents>
          <Section.Sticky>
            <ImageWrapper
              initial={{ opacity: 0 }}
              whileInView={{
                opacity: 1,
                transition: {
                  duration: 0.4,
                  delay: 0.1,
                },
              }}
              viewport={{ once: true }}
            >
              <MakeInformedDecisionsImage />
            </ImageWrapper>
          </Section.Sticky>
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

export default MakeInformedDecisions

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`
const ButtonWrapper = styled.div``

const ImageWrapper = styled(m.div)``
